import React, {Component, ReactNode} from 'react';
import FileUploader, {UploadProgress} from '../../sandbox/FileUploader';
import LanguageUtil from '../../../util/LanguageUtil';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import {InstanceContainer} from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomListService from '../../../service/CustomListService';
import ServiceContainer from '../../../config/ServiceContainer';
import {ServiceNames} from '../../../constant/ServiceNames';
import MessageUtil from '../../../util/MessageUtil';
import CustomListImportErrorUtil from '../../../util/CustomListImportErrorUtil';
import {CustomListBulkRecordType, CustomListTabType} from '../../../domain/custom-list/CustomListDomains';
import CustomLoading from '../../custom/CustomLoading';

interface Props {
  listId: string;
  recordType: CustomListTabType;
  onSuccess: () => void;
}

class State {
  public file: File | null = null;
  public percent: number = 0;
  public progress: UploadProgress = 'idle';
  public uploading: boolean = false;
}

class CustomListEmptyImportComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private readonly customListService: CustomListService = ServiceContainer.getInstance().get(ServiceNames.CUSTOM_LIST);

  public render(): ReactNode {
    const {file, percent, progress, uploading} = this.state;

    return (
      <div className={'flex w-full h-full items-center pb-2 2xl:pb-3 justify-center'}>
        <div className={'flex flex-col w-full h-full min-h-[320px] border-2 border-dashed border-[#E0E4E8] rounded-[10px] bg-main-gray'}>
          {uploading ? (
            <div className={'flex w-full h-full items-center justify-center'}>
              <CustomLoading/>
            </div>
          ) : (
            <FileUploader
              acceptedFiles={['text/csv']}
              file={file}
              percent={percent}
              progress={progress}
              onChangeFile={this.onChangeFile}
              maxBytes={this.environmentUtil.getStorageMaxSize()}
              disabled={uploading}
            />
          )}
        </div>
      </div>
    );
  }

  private onChangeFile = async (selectedFile: File | null): Promise<void> => {
    const {listId, recordType} = this.props;

    if (selectedFile == null) {
      this.setState({file: null, progress: 'idle', percent: 0});
      return;
    }

    const type: CustomListBulkRecordType = this.customListService.toBulkRecordType(recordType);
    this.setState({file: selectedFile, uploading: true, progress: 'uploading', percent: 0});
    try {
      await this.customListService.addBulk(listId, type, selectedFile);
      this.setState({uploading: false, progress: 'uploaded', percent: 100, file: null});
      MessageUtil.success({
        summary: LanguageUtil.get({id: 'pages.customList.importSuccess', defaultMessage: 'CSV imported successfully'}),
      });
      this.props.onSuccess();
    } catch (e) {
      console.error(e);
      this.setState({uploading: false, progress: 'error'});
      MessageUtil.error({
        summary: CustomListImportErrorUtil.resolveErrorMessage(e, this.props.recordType),
      });
    }
  };
}

export default CustomListEmptyImportComponent;
